"use strict";

/*!
 * GiocoVilla 3D 1.0.0
 * www.palladiomusem.org
 * © Simone Baldissini
 */
import * as THREE from "three";
import Stats from "three/examples/jsm/libs/stats.module";
import {GridHelper2} from "./GridHelper2";
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls";
import {SnapDragControls} from "./SnapDragControls";
import {RoundedBoxGeometry} from "three/examples/jsm/geometries/RoundedBoxGeometry";
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader";
import {SetClipperInitCallback, ClipperLib} from "./ClipperLib";
import { SVG } from '@svgdotjs/svg.js'
import gsap from "gsap";

/**
 * Esito della soluzione del gioco.
 * @type {Readonly<Object>}
 * @property {number} SOLVE_PALLADIO Gioco risolto secondo la soluzione di Palladio.
 * @property {number} SOLVE_ALTERNATIVE Gioco risolto con una soluzione alternativa a quella di Palladio.
 * @property {number} ERR_SALE Alcune stanze sporgono al di fuori della pianta della villa.
 * @property {number} ERR_OVERLAP Errore, alcune stanze si sovrappongono.
 * @property {number} ERR_SYMMETRY1 Asimmetria nel numero di stanze a destra e a sinistra.
 * @property {number} ERR_SYMMETRY2 Asimmetria nella disposizione delle stanze.
 * @property {number} ERR_INCOMPLETE Mancano stanze per completare la soluzione.
 */
const SolveEnum = Object.freeze({
    SOLVE_PALLADIO: 0,
    SOLVE_ALTERNATIVE: 1,
    ERR_SALE: -11,
    ERR_OVERLAP: -13,
    ERR_SYMMETRY1: -14,
    ERR_SYMMETRY2: -15,
    ERR_INCOMPLETE: -16
});

/**
 * Posizione di una stanza.
 * @type {Readonly<Object>}
 * @property {number} OUTSIDE_GROUND Stanza al di fuori del piano di gioco.
 * @property {number} OUTSIDE_PLAN Stanza al di fuori della pianta della villa.
 * @property {number} INSIDE_PLAN Stanza all'interno della pianta della villa.
 */
const StanzaPos = Object.freeze({
    INSIDE_PLAN: 0,
    OUTSIDE_PLAN: 1,
    OUTSIDE_GROUND: 2
});

class InfoStanza {
    /** @member {number} level Tipologia di stanza **/
    level = 1;
    data = [];
    // noinspection JSUnusedGlobalSymbols
    closed = true;
    id = null;
    /** @member {THREE.Object3D} room */
    room = null;
    size;
    /** @member {number} pos */
    pos;
    /** @member {boolean} rotated */
    rotated;
    /** @member {number} #base_w */
    #base_w;
    /** @member {number} #base_h */
    #base_h;
    /** @member {number} #grid_w */
    #grid_w;
    /** @member {number} #grid_h */
    #grid_h;
    /** @member {ClipperLib.PathsD} #paths */
    paths = null;
    rect = null;
    /** @member {Villa} #villa */
    #villa = {};

    /**
     * @param {THREE.Object3D} room
     * @param {Villa} villa
     */
    constructor(room, villa)
    {
        this.level = GiocoVilla.validateRoomType(room.userData.level);
        this.id = room.id;
        this.room = room;

        this.#villa  = villa;
        this.#base_w = villa['base_w'];
        this.#base_h = villa['base_h'];
        this.#grid_w = villa['grid_w'];
        this.#grid_h = villa['grid_h'];

        this.refresh();
    }

    /**
     * @returns {number}
     */
    get minX() {
        return this.room.position.x - this.size.w / 2;
    }
    /**
     * @returns {number}
     */
    get maxX() {
        return this.minX + this.size.w;
    }

    /**
     * @returns {number}
     */
    get cenX() {
        return this.room.position.x; // this.minX + this.size.w / 2;
    }

    /**
     * @returns {number}
     */
    get minY() {
        return this.room.position.z - this.size.h / 2;
    }
    /**
     * @returns {number}
     */
    get maxY() {
        return this.minY + this.size.h;
    }

    /**
     * @returns {number}
     */
    get cenY() {
        return this.room.position.z; // this.minY + this.size.h / 2;
    }

    /**
     * @returns {number}
     */
    get roomCounter() {
        return this.room.userData.counter || -1;
    }

    delete()
    {
        if (this.paths) {
            GiocoVilla.releasePathsD(this.paths);
            this.paths = null;
        }
    }

    /**
     * Aggiorna i dati relativi alla posizione della stanza.
     */
    refresh()
    {
        this.rotated = this.room.rotation.y !== 0;
        this.size = GiocoVilla.getRoomSize(this.level, this.rotated);
        const x = this.room.position.x;
        const y = this.room.position.z;
        let points = [
            {x: x - this.size.w/2, y: y - this.size.h/2},
            {x: x + this.size.w/2, y: y - this.size.h/2},
            {x: x + this.size.w/2, y: y + this.size.h/2},
            {x: x - this.size.w/2, y: y + this.size.h/2}
        ];
        if (this.paths) {
            // Rilascia i dati di Clipper.
            for (let i = 0, c = this.paths.size(); i < c; i++) {
                this.paths.get(i).delete();
            }
            this.paths.delete();
        }
        this.paths = new ClipperLib.PathsD();

        const path = GiocoVilla.createPathD(points, true);
        points = path.toArray();

        this.data = points;
        this.paths.push_back(path);
        this.rect = {left: this.minX, top: this.minY, right: this.maxX , bottom: this.maxY}

        // Verifica la posizione della stanza rispetto alla pianta.
        if (this.room.position.x < -this.#base_w / 2 || this.room.position.x > this.#base_w / 2 || this.room.position.z < -this.#base_h / 2 || this.room.position.z > this.#base_h / 2) {
            // Stanza trascinata fuori dal piano del gioco.
            this.pos = StanzaPos.OUTSIDE_GROUND;
        } else {
            // Ottiene l'area di intersezione tra la pianta dell'edificio e la stanza.
            const p = ClipperLib.RectClip(this.rect, this.#villa.planD);
            let area = ClipperLib.AreaPaths(p);
            // console.log('area %f', area);
            p.delete();
            this.pos = area === 0 ? StanzaPos.OUTSIDE_PLAN : StanzaPos.INSIDE_PLAN;
        }
    }
}

/**
 * Id del layer per gli elementi grafici.
 * @constant {number} LAYER_UI
 */
const LAYER_UI = 1;
/**
 * Id del layer per la geometria della copertura.
 * @constant {number} LAYER_COPERTURA
 */
const LAYER_COPERTURA = 2;
const LAYER_STANZE_FISSE = 3;

/**
 * @typedef {Object} Villa
 * @property {String} name
 * @property {String} title
 * @property {number} h Altezza delle stanze.
 * @property {number} h_ground Posizione verticale a cui collocare il terreno.
 * @property {number} base_w Larghezza del terreno.
 * @property {number} base_h Profondità del terreno.
 * @property {number} [base_w2_scale] Fattore di scala applicato alla larghezza del terreno con la villa completa di barchesse.
 * @property {number} [base_h2_scale] Fattore di scala applicato alla profondità del terreno con la villa completa di barchesse.
 * @property {number} grid_w Larghezza della griglia del piano.
 * @property {number} grid_h Profondità della griglia del piano.
 * @property {number} grid_step Fattore moltiplicativo della griglia del piano.
 * @property {Array.<number>} plan Coordinate della superficie della pianta.
 * @property {ClipperLib.PathsD} planD Poligono della pianta.
 * @property {number} piani
 * @property {{x: number, y: number, z: number}} new_origin Coordinate in cui inserire le nuove stanze.
 * @property {?ClipperLib.PathsD} soluzioni_aree
 * @property {Array.<Object>} models
 * @property {Array.<{sala: number, x: number, y: number, r: boolean}>} soluzioni
 */
class GiocoVilla extends EventTarget {
    // region Inizializzazione
    // region Properties

    canvas = null;
    /** @member {?Villa} #villa */
    #villa = null;
    #initialized = false;
    /** @member {THREE.Scene} #scene */
    #scene = null;
    /** @member {THREE.WebGLRenderer} #renderer */
    #renderer = null;
    /** @member {THREE.PerspectiveCamera} #camera */
    #camera = null;
    /** @member {THREE.Mesh} #ground Piano del terreno. */
    #ground = null;
    /** @member {GridHelper2} #gridHelper Griglia su cui disporre le stanze. */
    #gridHelper = null;
    /** @member {THREE.Audio} #popAudio1 */
    #popAudio1 = null;
    /** @member {THREE.Audio} #popAudio2 */
    #popAudio2 = null;
    debug = true;
    /** @member {THREE.LineSegments} #sagoma Sagoma in pianta della stanza che si sta trascinando. */
    #sagoma = null;
    /** Gruppo delle stanze aggiunte nella partita. */
    #rooms = null;
    /** @member {THREE.Group} #standard_rooms Gruppo delle stanze predefinite. */
    #standard_rooms = null;
    /** @member {OrbitControls} #oControls Gestore dell'esplorazione interattiva del modello. */
    #oControls = null;
    /** @member {SnapDragControls} #dControls Gestore del trascinamento delle stanze. */
    #dControls = null;
    /** @member {Stats} $stats */
    #stats = null;
    #continuousRefreshCounter = 0;
    #isContinuousRefresh = false;
    #animationFrameId = 0;
    #status = SolveEnum.ERR_INCOMPLETE;
    /** Indica se la villa è completamente visibile, compresa la copertura. */
    #completeVisible = false;
    /** @type {Map<string, InfoStanza>} */
    #aree_stanze = new Map();
    #soluzioniInitialized = false;
    /** @member {?THREE.Object3D} #dragging_room Riferimento alla stanza che si sta trascinando. */
    #dragging_room = null;
    /** @member {?THREE.Object3D} #dragging_room_wireframe Riferimento al box in fil di ferro della stanza che si sta trascinando. */
    #dragging_room_wireframe = null;
    #room_counter = 0;
    #ray_caster = null;

    /** @member {SVG} #svg Pianta SVG. */
    #svg = null;
    /** @member {number} #svg_scale Fattore di ingrandimento applicato alla pianta svg. */
    #svg_scale = 6;
    /** @member {SVGElement} #svgDragged Stanza SVG in corso di trascinamento. */
    #svgDragged = null;
    /** @member {function} #svg_onMouseUp Handler del mouse up per le stanze SVG. */
    #svg_onMouseUp = null;
    /** @member {function} #svg_onMouseMove Handler del mouse move per le stanze SVG. */
    #svg_onMouseMove = null;
    #resource_dir = '';
    /** @member {{debug: boolean, roundbox: boolean, svg_scale: number}} options */
    options = {};
    // endregion

    /**
     * Inizializza il gioco.
     * @param {string|URL} url Url dal quale caricare i dati della villa in formato JSON.
     * @param {HTMLCanvasElement} canvas Riferimento al canvas su cui andare a disegnare.
     * @param {string} resource_dir Percorso da cui caricare le risorse.
     * @param {Object} options Impostazioni del gioco.
     * @param {boolean} [options.debug] Indica se usare la modalità di debug, predefinito false.
     * @param {boolean} [options.roundbox] Indica se arrotondare gli angoli delle stanze, prefefinito true.
     */
    constructor(url, canvas, resource_dir = '../', options = {})
    {
        super();

        this.canvas = canvas;
        if (resource_dir !== "" && resource_dir.slice(-1) !== "/") {
            resource_dir += "/";
        }
        this.#resource_dir = resource_dir;

        this.options = Object.assign({debug: false, roundbox: true, svg_scale: this.#svg_scale}, options);
        this.debug = !!this.options.debug;
        this.#svg_scale = this.options.svg_scale;

        this.#initScene();

        SetClipperInitCallback(() => {
            if (this.debug) { console.info('Clipper initialized'); }
            if (this.isLoaded) {
                this.#initAreeSoluzione();
            }
            this.#onReady();
        });

        if (url) {
            // Carica i dati del gioco.
            fetch(url)
                .then((response) => response.json())
                .then((data) => this.#initVilla(data))
            ;
        }

        // Impostazioni predefinite di Gsap. Notare che se queste opzioni vengono reimpostate per le singole chiamate
        // a gsap le impostazioni predefinite non vengono considerate.
        gsap.defaults({
            onStart: () => {
                // if (this.debug) { console.log("tween is staring"); }
                // Avvia il refresh continuativo.
                this.startContinuousRefresh();
            },
            onComplete: () => {
                // if (this.debug) { console.log("tween is complete"); }
                // Termina il refresh continuativo.
                this.stopContinuousRefresh();
                this.refresh();
            },
            onInterrupt: () => {
                // if (this.debug) { console.warn("tween is interrupted"); }
                // Termina il refresh continuativo.
                this.stopContinuousRefresh();
            },
            /*
            onUpdate: () => {
                console.log("fires if tween is running");
            },*/
        });

        this.#svg_onMouseUp = (event) => { return this.#onSVGMouseUp(event); };
        this.#svg_onMouseMove = (event) => { return this.#onSVGMouseMove(event); };
    }

    /**
     * Carica un nuovo file di gioco.
     * @param {URL} url
     * @returns {Promise<GiocoVilla>}
     */
    load(url) {
        this.#initialized = false;
        if (this.isLoaded) {
            if (this.#completeVisible) {
                this.hideComplete();
            }
            if (this.debug) {
                console.group('Inizializzazione dei contenuti…');
            }
            this.#release();

            this.#scene.remove(this.#gridHelper);
            this.#gridHelper = null;

            this.#scene.remove(this.#ground);
            this.#ground = null;
            if (this.#svg) {
                this.#svg.clear();
                this.#svg.remove();
            }
            this.#dControls.dispose();
            this.#dControls = null;

            const geo = [];
            this.#scene.traverse((object) => {
                if (object.userData.gioco) {
                    geo.push(object);
                }
            });
            geo.forEach((object) => { object.removeFromParent(); });

            this.#villa = null;
            this.#requestRefresh();

            if (this.debug) {
                console.groupEnd();
            }
        }

        return new Promise((resolve, reject) => {
            fetch(url)
                .then((response) => response.json())
                .then((data) => {
                    this.#initVilla(data);
                    if (ClipperLib.initialized) {
                        this.#initAreeSoluzione();
                        this.#onReady();
                    }
                    resolve(this);
                })
            ;
        });

    }

    /**
     * Rilascia le risorse allocate dal gioco.
     */
    #release() {
        if (this.isLoaded) {
            if (this.#villa['soluzioni_aree']) {
                GiocoVilla.releasePathsD(this.#villa.soluzioni_aree);
                this.#villa.soluzioni_aree = null;
            }
            this.#soluzioniInitialized = false;

            for (let name in this.#villa.models) {
                if (!this.#villa.models.hasOwnProperty(name)) {
                    continue;
                }
                const model = this.#villa.models[name];
                GiocoVilla.releasePathsD(model.paths);
                delete model.paths;
            }
            GiocoVilla.releasePathsD(this.#villa.planD);
            delete this.#villa.planD;
        }
        this.#aree_stanze.forEach((area) => { area.delete(); });
        this.#aree_stanze.clear();
        // TODO Ricordarsi di rilasciare aree e oggetti di ClipperLib.
    }

    destroy()
    {
        if (this._listeners) {
            this._listeners.length = 0;
        }
        this.#release();
        this.#aree_stanze.clear();
    }

    /**
     * Indica se i parametri della villa sono stati caricati.
     * @returns {boolean}
     */
    get isLoaded() {
        return this.#villa !== null;
    }

    /**
     * Indica se la partita è risolta.
     * @returns {boolean}
     */
    get isRisolto() {
        return this.#status === SolveEnum.SOLVE_PALLADIO || this.#status === SolveEnum.SOLVE_ALTERNATIVE;
    }

    get status() {
        return this.#status;
    }

    /**
     * @param {number} value
     * @param {boolean} sendEvent Indica se inviare l'evento di soluzione del gioco.
     * @param {boolean} auto Indica se la soluzione del gioco è stata ottenuta automaticamente.
     * @param {boolean} force Indica se inviare l'evento anche se lo stato non è cambiato.
     */
    #setStatus(value, sendEvent = true, auto = false, force = false) {
        if (value === this.#status && !force) {
            this.#gridHelper.visible = !this.isRisolto;
            return;
        }
        this.#status = value;
        // Nasconde la griglia se il gioco è risolto.
        const solved = this.isRisolto;
        this.#gridHelper.visible = !solved;
        if (sendEvent) {
            this.dispatchEvent(new CustomEvent('gioco_solved', {detail: {status: value, solved, auto}}));
        }
    }

    get name() {
        return this.#villa.name || "N/D";
    }
    get villa() {
        return this.#villa;
    }
    /**
     * Inizializza l'ambiente 3D.
     */
    #initScene() {
        this.#scene = new THREE.Scene();

        this.#renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
        this.#renderer.setClearColor(0xefefef);
        const rect = this.canvas.getBoundingClientRect();
        // this.#renderer.setSize(window.innerWidth, window.innerHeight);
        // this.#renderer.setPixelRatio(window.devicePixelRatio); // https://r105.threejsfundamentals.org/threejs/lessons/threejs-responsive.html
        this.#renderer.shadowMap.enabled = true;
        this.#renderer.shadowMap.type = THREE.PCFSoftShadowMap; // default THREE.PCFShadowMap

        //const aspect = window.innerWidth / window.innerHeight;
        const aspect = rect.width / rect.height;
        this.#camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 1000);
        this.#camera.layers.enable(LAYER_UI); // Mostra gli elementi grafici di supporto.
        this.#camera.layers.disable(LAYER_COPERTURA); // Nasconde la copertura.
        this.#camera.position.set(15, 15, 15);
        this.#camera.lookAt(0,0,0);
        this.#scene.add(this.#camera);

        // create an AudioListener and add it to the camera
        const listener = new THREE.AudioListener();
        this.#camera.add( listener );

        // region Luci
        const ambient = new THREE.AmbientLight(0x404040, 1)
        this.#scene.add(ambient);

        const max_intensity = 3;
        // Create a DirectionalLight and turn on shadows for the light
        const light1 = new THREE.DirectionalLight( 0xffffff, .5 );
        light1.position.set( -6, 30, 6 );
        light1.target.position.set( 6.25, 0, -12.5 );
        light1.castShadow = true;
        light1.layers.set(LAYER_UI);
        const d = 40;
        light1.shadow.camera.left = - d;
        light1.shadow.camera.right = d;
        light1.shadow.camera.top = d;
        light1.shadow.camera.bottom = - d;
        //Set up shadow properties for the light
        light1.shadow.mapSize.width = 4096;
        light1.shadow.mapSize.height = 4096;
        light1.shadow.camera.near = 0.5;
        light1.shadow.camera.far = 500;

        this.#scene.add( light1 );
        this.#scene.add( light1.target );

        const light2 = light1.clone();
        light2.castShadow = false;
        light2.intensity = max_intensity - light1.intensity;
        light2.layers.set(LAYER_UI);
        this.#scene.add( light2 );
        this.#scene.add( light2.target );

        /*
        //Create a helper for the shadow camera (optional)
        const helper = new THREE.CameraHelper( light1.shadow.camera );
        this.#scene.add( helper );
        */

        // create a global audio source
        this.#popAudio1 = new THREE.Audio( listener );
        try { this.#popAudio1.stop(); } catch(e) { }
        this.#popAudio2 = new THREE.Audio( listener );
        try { this.#popAudio2.stop(); } catch(e) { }

        // load a sound and set it as the Audio object's buffer
        const audioLoader = new THREE.AudioLoader();
        audioLoader.load( this.#resource_dir + 'media/pop.mp3', ( buffer ) => {
            this.#popAudio1.setBuffer( buffer );
            try {
                this.#popAudio1.stop();
            } catch(e) { }
            this.#popAudio1.setVolume(0.5);
            this.#popAudio1.setLoop( false );
        });
        audioLoader.load( this.#resource_dir + 'media/pop2.wav', ( buffer ) => {
            this.#popAudio2.setBuffer( buffer );
            try {
                this.#popAudio2.stop();
            } catch(e) { }
            this.#popAudio2.setVolume(0.5);
            this.#popAudio2.setLoop( false );
        });

        /** Gruppo delle stanze da animare. */
        this.#rooms = new THREE.Group();
        this.#scene.add(this.#rooms);
        this.#standard_rooms = new THREE.Group();
        this.#standard_rooms.userData.isStdRoom = true;
        this.#standard_rooms.layers.set(LAYER_STANZE_FISSE);
        this.#scene.add(this.#standard_rooms);

        this.#sagoma = (() => {
            const geo = new THREE.EdgesGeometry( new THREE.PlaneGeometry(1, 1) );
            const mat = new THREE.LineBasicMaterial( { color: 0x00ffff } );
            return new THREE.LineSegments( geo, mat );
        })();
        this.#sagoma.layers.set(LAYER_UI);
        this.#sagoma.geometry.rotateX(-Math.PI / 2);
        this.#scene.add(this.#sagoma);

        if (this.debug) {
            // stats
            this.#stats = new Stats();
            this.#stats.domElement.style.position = 'absolute';
            this.#stats.domElement.style.left = '0px';
            this.#stats.domElement.style.top = '0px';
            this.#renderer.domElement.parentElement.appendChild( this.#stats.domElement );

            const axes = new THREE.AxesHelper(31);
            axes.layers.set(LAYER_UI);
            axes.position.set(0, 0.015, 0);
            this.#scene.add(axes);
        }

        this.#oControls = new OrbitControls(this.#camera, this.#renderer.domElement);
        this.#oControls.minDistance = 10;
        this.#oControls.maxDistance = 100;
        this.#oControls.addEventListener('start', () => { this.#requestRefresh() } );
        this.#oControls.addEventListener('change', () => {
            if (this.#dControls && this.#dControls.enabled && !this.#dControls.isPending) {
                this.#dControls.enabled = false;
                if (this.debug) {
                    console.log('disable drag');
                }
            }
            this.#requestRefresh();
        });
        this.#oControls.addEventListener('end', () => {
            // console.log('enable drag');
            if (this.#dControls && !this.#completeVisible) {
                this.#dControls.enabled = true;
                if (this.debug) {
                    console.log('enable drag');
                }
            }
            this.#requestRefresh();
        });

        this.#ray_caster = new THREE.Raycaster();
        this.#ray_caster.layers.enable(0); // Considera le stanze.
        this.#ray_caster.layers.enable(LAYER_STANZE_FISSE);
        this.#ray_caster.layers.disable(LAYER_UI); // Ignora gli oggetti sul layer uno (i bounding box e altri elementi di supporto)
        this.#ray_caster.layers.disable(LAYER_COPERTURA);

        // Double Click Event.
        this.#renderer.domElement.addEventListener('dblclick', (event) => { this.#onDblclick(event) });
        if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            const f = (event) => { this.#onDblclick(event); }
            this.#registerDoubleTapClosure(this.#renderer.domElement, f);
        }
        window.addEventListener('resize', () => { this.#onResize() }, false);
        this.#onResize();
    }

    /**
     * @param {SVGElement} element
     * @param {Function} callback
     */
    #registerDoubleTapClosure(element, callback) {
        element.addEventListener('touchstart', this.#detectDoubleTapClosure(callback), { passive: false });
    }

    /* Based on this http://jsfiddle.net/brettwp/J4djY/*/
    #detectDoubleTapClosure(callback) {
        let lastTap = 0;
        let timeout;
        const me = this;
        return function detectDoubleTap(event) {
            const curTime = new Date().getTime();
            const tapLen = curTime - lastTap;
            if (tapLen < 500 && tapLen > 0) {
                if (me.debug) {
                    console.log('Double tapped!', event);
                }
                callback(event);
            } else {
                timeout = setTimeout(() => {
                    clearTimeout(timeout);
                }, 500);
            }
            lastTap = curTime;
        };
    }


    /**
     * Inizializza le componenti della villa, dopo aver caricato i dati json.
     * @param {Villa} villa
     */
    #initVilla(villa) {
        if (this.debug) {
            console.group('Inizializzazione dei contenuti…');
            console.log(villa);
        }
        this.#villa = villa;
        for (let i = 0, c = villa.soluzioni.length; i < c; i++) {
            const soluzione = villa.soluzioni[i];
            for (let j = 0, k = soluzione.length; j < k; j++) {
                soluzione[j].sala = GiocoVilla.validateRoomType(soluzione[j].sala);
                soluzione[j].r = !!soluzione[j]['r'];
            }
        }

        // region Terreno
        this.#gridHelper = new GridHelper2(villa.grid_w * villa.grid_step, villa.grid_h * villa.grid_step, 1 / villa.grid_step, 0x999999);
        this.#gridHelper.position.set(0, .01, 0); // Centra la griglia ponendola appena sopra il piano di appoggio.
        this.#gridHelper.layers.set(LAYER_UI);
        this.#scene.add(this.#gridHelper);

        const groundGeo = new THREE.PlaneGeometry(villa.base_w, villa.base_h, 1, 1);
        const groundMat = new THREE.MeshStandardMaterial({color: 0xcccccc});
        groundGeo.rotateX(-Math.PI / 2);
        this.#ground = new THREE.Mesh(groundGeo, groundMat);
        this.#ground.receiveShadow = true;
        this.#ground.layers.set(LAYER_UI);
        this.#ground.position.set(0, -this.#villa.h_ground, 0);
        this.#scene.add(this.#ground);

        const loader = new GLTFLoader();

        // Disegna la pianta in 2D tramite SVG.
        this.#svg = SVG()
            .addTo(this.canvas.parentElement || 'body')
            .size(villa['base_w'] * this.#svg_scale, villa['base_h'] * this.#svg_scale)
            .viewbox(-(villa['base_w'] / 2) * this.#svg_scale, -(villa['base_h'] / 2) * this.#svg_scale, villa['base_w'] * this.#svg_scale, villa['base_h'] * this.#svg_scale);
        this.#svg.css({
            position: "absolute"/*, left: "100px"*/,
            margin: '5px',
            border: '1px solid rgba(0,0,0,.25)',
            'border-radius': '5px',
        });
        // Disegna il piano del terreno.
        const svgGround = this.#svg.rect(villa['base_w'] * this.#svg_scale, villa['base_h'] * this.#svg_scale).attr({
            fill: '#cccccc',
            opacity: .25,
            x: -(villa['base_w'] / 2) * this.#svg_scale,
            y: -(villa['base_h'] / 2) * this.#svg_scale,
            name: 'ground',
        });
        // Disegna il piano della villa.
        let coords = '';
        for (let i = 0, c = villa.plan.length; i < c; i += 2) {
            coords += (i === 0 ? 'M' : 'L') + (villa.plan[i] * this.#svg_scale) + ' ' + (-villa.plan[i + 1] * this.#svg_scale);
        }
        coords += "z";
        const svgBase = this.#svg.path(coords)
            .attr({
                fill: '#333333',
                stroke: '#333333',
                'stroke-width': 2,
                name: 'base',
            });

        // Disegna la griglia sul piano 2D.
        coords = '';
        for (let r = 0; r <= this.#villa.grid_h * villa.grid_step/2; r++) {
            const y = (-this.#villa.grid_h / 2 + (1/villa.grid_step)*2 * r) * this.#svg_scale;
            coords += 'M '+((-this.#villa.grid_w / 2) * this.#svg_scale - .5)+' '+y+' l'+((this.#villa.grid_w + .5) * this.#svg_scale)+' 0';
        }
        /*
        for (let c = 0; c <= this.#villa.grid_w * villa.grid_step/2; c++) {
            const x = (-this.#villa.grid_w / 2 + (1/villa.grid_step)*2 * c) * this.#svg_scale;
            coords += 'M '+x+((-this.#villa.grid_h / 2) * this.#svg_scale)+' l 0 '+(this.#villa.grid_h * this.#svg_scale)+'';
        }
        */
        this.#svg.path(coords)
            .attr({
                stroke: '#999999',
                'stroke-opacity': 1,
                'stroke-width': 1,
                'stroke-miterlimit': 1,
                'stroke-dasharray': '1 ' + (this.#svg_scale -1),
                'stroke-linecap': 'butt',
                name: 'grid',
            });

        const current_villa = this.#villa.name;

        // Aggiunge le stanze statiche.
        for (let name in this.#villa.models) {
            if (!this.#villa.models.hasOwnProperty(name)) {
                continue;
            }
            const model = this.#villa.models[name];

            if (model['plan'] || model['svg']) {
                // Disegna la pianta SVG della stanza.
                const points0 = model['svg'] ? model['svg'] : [model['plan']];
                coords = '';
                for (let j = 0, k = points0.length; j < k; j++) {
                    const points = points0[j];
                    for (let i = 0, c = points.length; i < c; i += 2) {
                        coords += (i === 0 ? 'M' : 'L') + (points[i] * this.#svg_scale) + ' ' + (-points[i + 1] * this.#svg_scale);
                    }
                    coords += 'z ';
                }
                const e = this.#svg.path(coords)
                    .attr({
                        fill: model.color,
                        stroke: model.stroke || '#333333',
                        'stroke-width': 1,
                        name: name
                    });
                if (!model.href || name === 'base') {
                    e.back();
                }
            }

            if (model.href) {
                // Carica il modello 3D della stanza.
                let material = new THREE.MeshStandardMaterial({
                    color: model.color,
                    side: THREE.FrontSide /*DoubleSide*/
                });
                material.transparent = true;
                let model_href;
                if (model.href.startsWith('/') || model.href.startsWith('http:') || model.href.startsWith('https:')) {
                    model_href = model.href;
                } else {
                    model_href = this.#resource_dir + model.href;
                }
                loader.load(model_href, (gltf) => {
                    if (current_villa !== this.#villa.name) {
                        // Il modello caricato era stato richiesto per una villa diversa da quella villa attuale.
                        return;
                    }
                    gltf.scene.traverse((o) => {
                        if (o.isMesh) {
                            o.material = material;
                            o.castShadow = true;
                            o.receiveShadow = true;
                            o.layers.set(model.visible ? 0 : LAYER_COPERTURA);
                        }
                    });
                    const g = new THREE.Group();
                    g.name = name;
                    g.userData.gioco = true;
                    g.userData.isStdRoom = true;
                    // g.visible = model.visible;
                    g.layers.set(model.visible ? LAYER_STANZE_FISSE : LAYER_COPERTURA);
                    g.add(gltf.scene);
                    if (model.stanza) {
                        this.#standard_rooms.add(g);
                    } else {
                        if (!model.visible) {
                            g.userData.villa = true;
                        }
                        this.#scene.add(g);
                    }
                    /*
                    if (model.plan) {
                        // Disegna la pianta della sala.
                        let coords = [];
                        for (let i=0; i <model.plan.length; i+=2) {
                            coords.push(new THREE.Vector2(model.plan[i], model.plan[i+1]));
                        }
                        let polyShape = new THREE.Shape(coords);
                        const polyGeometry = new THREE.ShapeGeometry(polyShape);
                        // polyGeometry.setAttribute("position", new THREE.Float32BufferAttribute(measurement.coordinates.map(coord => [coord.x, coord.y, coord.z]).flat(), 3))
                        let polygon = new THREE.Mesh(polyGeometry, material);
                        polygon.castShadow = true;
                        polygon.receiveShadow = true;
                        polygon.rotation.x = -Math.PI/ 2;
                        polygon.position.y = .01;
                        polygon.userData.gioco = true;
                        polygon.layers.set(model.visible ? 0 : LAYER_COPERTURA);
                        this.#scene.add(polygon);
                        g.visible = false;
                    }
                    */
                    this.#requestRefresh();
                }, undefined, (error) => {
                    console.error('Error loading room', model_href, error);
                });
            }
        }
        // Ridistribuisce lo sfondo della pianta svg.
        svgBase.back();
        svgGround.back();
        this.#dControls = new SnapDragControls(this.#scene, this.#camera, this.#renderer.domElement, .5, this.#villa.base_w + 10, this.#villa.base_h + 10, this.#validaSelezioneStanza, this.debug);
        const layers = this.#dControls.getRaycaster().layers;
        layers.enable(0);
        layers.enable(LAYER_STANZE_FISSE);
        layers.disable(LAYER_UI); // Disabilita il gestore del drag per gli elementi di interfaccia e dela copertura della villa.
        layers.disable(LAYER_COPERTURA);
        this.#dControls.addEventListener('dragstart', (event) => {
            this.#onDragStart(event);
        });
        this.#dControls.addEventListener('drag', (event) => {
            this.#onDrag(event);
        });
        this.#dControls.addEventListener('dragend', (event) => {
            this.#onDragEnd(event);
        });

        this.#dControls.addEventListener('hoveron', (event) => {
            this.#onHoverOn(event);
        });
        this.#dControls.addEventListener('hoveroff', (event) => {
            this.#onHoverEnd(event);
        });

        this.#initAreeSoluzione();
        if (this.debug) {
            console.groupEnd();
        }
        this.#requestRefresh();

        this.#onReady();
    }

    #onReady()
    {
        if (!this.#initialized && this.isLoaded && (typeof ClipperLib !== 'undefined') && ClipperLib.initialized) {
            this.#initialized = true;
            window.setTimeout(() => { this.dispatchEvent(new CustomEvent('gioco_ready')); }, 0);
        }
    }

    /**
     * Inizializza le aree della soluzione del gioco.
     */
    #initAreeSoluzione()
    {
        if (typeof ClipperLib == 'undefined' || typeof ClipperLib['PathsD'] == 'undefined' || !this.isLoaded || this.#soluzioniInitialized) {
            // Clipper non ancora disponibile.
            return;
        }
        if (this.debug) { console.log('initAreeSoluzione'); }

        // Determina le aree che dovranno essere campite.
        this.#soluzioniInitialized = true;
        // ClipperLib.setDebug(true);
        const aree = new ClipperLib.PathsD();
        const soluzione = this.#villa.soluzioni[0];
        for (let j = 0 ; j < soluzione.length; j++) {
            const stanza = soluzione[j];
            const level = GiocoVilla.validateRoomType(stanza.sala);
            const size = GiocoVilla.getRoomSize(level, stanza.r);

            const cen_x = stanza.x;
            const cen_y = stanza.y;
            let points = [
                {x: cen_x - size.w/2, y: cen_y - size.h/2},
                {x: cen_x + size.w/2, y: cen_y - size.h/2},
                {x: cen_x + size.w/2, y: cen_y + size.h/2},
                {x: cen_x - size.w/2, y: cen_y + size.h/2}
            ];
            const path = GiocoVilla.createPathD(points, true);
            soluzione.path = path;
            soluzione.paths = new ClipperLib.PathsD();
            soluzione.paths.push_back(path);
            aree.push_back(soluzione.path);
        }
        const clip = new ClipperLib.PathsD();
        const union = ClipperLib.Union(aree, clip, ClipperLib.FillRule.NonZero, 2);
        GiocoVilla.releasePathsD(clip);
        GiocoVilla.releasePathsD(aree);
        // console.log('area da campire ', ClipperLib.AreaPaths(union));
        this.#villa.soluzioni_aree = union;

        const points = [];
        for (let i = 0, c = this.#villa.plan.length; i < c; i += 2) {
            points.push({x: this.#villa.plan[i], y: -this.#villa.plan[i + 1]});
        }
        const path = GiocoVilla.createPathD(points, true);
        const paths = new ClipperLib.PathsD();
        paths.push_back(path);
        this.#villa.planD = paths;
        this.#villa.area = ClipperLib.AreaPath(path);
    }
    // endregion


    // region Eventi 3D

    /**
     * Gestisce il ridimensionamento della finestra.
     */
    #onResize() {
        //const width = window.innerWidth;
        //const height = window.innerHeight;

        const canvas = this.#renderer.domElement;
        const pixelRatio = window.devicePixelRatio;
        const width = canvas.clientWidth * pixelRatio | 0;
        const height = canvas.clientHeight * pixelRatio | 0;
        const needResize = canvas.width !== width || canvas.height !== height;
        if (needResize) {
            this.#renderer.setSize(width, height, false);
            this.#camera.aspect = width / height;
            this.#camera.updateProjectionMatrix();

            this.#requestRefresh();
        }
    }

    /**
     * Gestisce il doppio click sulla scena.
     * @param {MouseEvent|TouchEvent} event
     */
    #onDblclick(event) {
        event.preventDefault();
        if (this.#completeVisible) {
            // Se la villa è interamente visibile con la copertura disabilita il gestore del doppio click.
            return;
        }
        this.#dControls.clearPending();
        // Calculate pointer position in normalized device coordinates
        const rect = this.canvas.getBoundingClientRect();

        let p = this.#clientPointOfEvent(event);
        // Create a normalised vector in 2d space
        const point = new THREE.Vector2(
             ( p.x - rect.left ) / rect.width  * 2 - 1,
            -( p.y - rect.top  ) / rect.height * 2 + 1
        );

        this.#ray_caster.setFromCamera(point, this.#camera);

        // Check if the ray has intersected with any objects and get intersections
        const intersects = this.#ray_caster.intersectObjects([...this.#standard_rooms.children, ...this.#rooms.children], true);
        // Check if intersected with objects
        if (intersects.length > 0) {
            let stanza = this.#validaSelezioneStanza(intersects[0].object, this.debug);
            if (!stanza) {
                if (this.debug) {
                    console.log('No room found for dbl');
                }
                return;
            }
            if (this.debug) {
                const color = 'color: #' + GiocoVilla.getRoomColor(stanza.userData.level).toString(16).padStart(6, '0');
                console.log('%cdblclick on%c \t%c%s%c, %d, %s', 'font-weight: bold; font-style: italic', 'font-weight: normal; font-style: normal', color, stanza.name, 'color: inherit');
            }
            if (this.#rotateRoom(stanza)) {
                this.#sagoma.rotation.y = stanza.rotation.y;
                this.test();
            }
        } else if (this.debug) {
            console.log('no dbl');
        }
    }

    /**
     * Ruota una stanza.
     * @param {THREE.Object3D} stanza
     * @returns {boolean}
     */
    #rotateRoom(stanza) {
        if (stanza.userData.level === 1) {
            if (this.debug) {
                console.warn('La stanza quadrata non può essere ruotata.');
            }
            return false;
        }
        stanza.rotation.y = stanza.rotation.y === 0 ? Math.PI / 2 : 0;
        this.playPop(1);
        this.#requestRefresh();
        const info = this.#refreshInfoStanza(stanza);
        this.#refreshSVGPosition(info);
        if (info.pos === StanzaPos.OUTSIDE_PLAN) {
            gsap.to(stanza.position, { duration: .15, y: this.#villa.h/2 - this.#villa.h_ground});
        } else {
            if (this.#roomHasIntersections(stanza)) {
                gsap.to(stanza.position, {duration: .15, y: this.#villa.h * 3 / 2});
            } else {
                gsap.to(stanza.position, {duration: .15, y: this.#villa.h / 2});
            }
        }
        return true;
    }

    /**
     * @param {Object} event
     * @param {THREE.Object3D} event.object
     */
    #onDragStart(event)
    {
        // console.log('disable orbit');
        this.#oControls.enabled = false; // Disable orbit controls while dragging.

        this.#gridHelper.visible = true;

        this.#dragging_room = event.object;
        this.#dragging_room_wireframe = this.#dragging_room.getObjectByName('wireframe');

        const info = this.#getInfoStanza(this.#dragging_room);
        if (this.debug) {
            const color = 'color: #' + GiocoVilla.getRoomColor(info.level).toString(16).padStart(6, '0');
            console.log('%cstart drag %c \t%c%s%c', 'font-weight: bold; font-style: italic', 'font-weight: normal; font-style: normal', color, info.room.name, 'color: inherit');
        }

        // Ricolloca la sagoma sotto alla stanza.
        this.#sagoma.rotation.y = this.#dragging_room.rotation.y;
        this.#sagoma.scale.set(this.#dragging_room.userData.w, 1, this.#dragging_room.userData.h);
        this.#sagoma.position.set(this.#dragging_room.position.x, (info.pos === StanzaPos.INSIDE_PLAN ? 0 : -this.#villa.h_ground)+0.02, this.#dragging_room.position.z);
        this.#sagoma.visible = true;

        const color = this.#roomHasIntersections(this.#dragging_room) ? 0xff00ff : 0x00ffff;
        // Aggiorna il colore della sagoma in pianta e del box della stanza in base al fatto che si intersechi con altre o no.
        this.#sagoma.material.color.set(color);
        this.#dragging_room_wireframe.material.color.set(color);

        // Anima il sollevamento della stanza.
        gsap.to(this.#dragging_room.position, { duration: .15, y: this.#villa.h * 3 / 2 + 1});
        gsap.to(this.#dragging_room_wireframe.material, { duration: .15, opacity: 1});

        this.#dragging_room.userData.svg.attr({stroke: '#00ffff'}).front();
        this.dispatchEvent(new CustomEvent('drag_room_start', { detail: info }));
    }

    /**
     * @param {Object} event
     * @param {THREE.Object3D} event.object
     */
    #onDrag(event)
    {
        this.#dragging_room = event.object;
        // Aggiorna le informazioni sulla stanza spostata.
        const info = this.#refreshInfoStanza(this.#dragging_room);

        let y = 0;
        this.#sagoma.material.color.set(0x00ffff);
        this.#dragging_room_wireframe.material.color.set(0x00ffff);
        this.#dragging_room.userData.svg.attr({stroke: '#00ffff'});

        if (info.pos === StanzaPos.INSIDE_PLAN) {
            // Stanza collocata all'interno della pianta della villa.
            this.#dragging_room.material.opacity = 1;
            if (this.#roomHasIntersections(this.#dragging_room)) {
                // Intersezione rilevata.
                this.#sagoma.material.color.set(0xff00ff);
                this.#dragging_room_wireframe.material.color.set(0xff00ff);
                this.#dragging_room.userData.svg.attr({stroke: '#ff00ff'});
            }
        } else {
            // Stanza trascinata fuori dalla pianta della villa.
            y = -this.#villa.h_ground;
            if (info.pos === StanzaPos.OUTSIDE_GROUND) {
                // Stanza trascinata fuori dal piano.
                this.#sagoma.material.color.set(0xff0000);
                this.#dragging_room_wireframe.material.color.set(0xff0000);
                this.#dragging_room.userData.svg.attr({stroke: '#ff0000'});
                this.#dragging_room.material.opacity = .5;
            } else {
                this.#dragging_room.material.opacity = 1;
            }
        }
        this.#sagoma.position.set(this.#dragging_room.position.x, y+0.02, this.#dragging_room.position.z);

        if (!this.#isContinuousRefresh) {
            this.refresh();
        }
        this.#refreshSVGPosition(info);
        this.dispatchEvent(new CustomEvent('drag_room_move', { detail: info }));
    }

    /**
     * @param {Object} event
     * @param {THREE.Object3D} event.object
     */
    #onDragEnd(event)
    {
        // console.log('enable orbit');
        this.#oControls.enabled = true;
        // this.#dragging_room = event.object;

        if (this.debug) {
            console.log('dest x: %f z: %f', this.#dragging_room.position.x, this.#dragging_room.position.z);
        }

        // Aggiorna le informazioni sulla stanza spostata.
        const info = this.#refreshInfoStanza(this.#dragging_room);
        if (this.debug) {
            const color = 'color: #' + GiocoVilla.getRoomColor(info.level).toString(16).padStart(6, '0');
            console.log('%cend drag %c \t%c%s%c', 'font-weight: bold; font-style: italic', 'font-weight: normal; font-style: normal', color, info.room.name, 'color: inherit');
        }
        if (info.pos === StanzaPos.OUTSIDE_GROUND) {
            // Elimina la stanza trascinata fuori dall'area di gioco.
            this.removeRoom(this.#dragging_room).finally(() => {
                this.#sagoma.visible = false;
            });
        } else {
            let y;
            if (info.pos === StanzaPos.INSIDE_PLAN) {
                if (this.#roomHasIntersections(this.#dragging_room)) {
                    y = this.#villa.h * 3 / 2;
                } else {
                    y = this.#villa.h / 2;
                }
            } else {
                y = this.#villa.h / 2 - this.#villa.h_ground;
            }
            const stanza = this.#dragging_room.position;
            // Anima la discesa della stanza.
            gsap.to(stanza, {
                duration: .15, y: y, onComplete: () => {
                    this.#sagoma.visible = false;
                    this.stopContinuousRefresh();
                    this.#requestRefresh();
                    this.test();
                }
            });
            const material = this.#dragging_room_wireframe.material;
            gsap.to(material, { duration: .15, opacity: 0});
        }

        this.#dragging_room.userData.svg.attr({stroke: '#333333'});

        this.dispatchEvent(new CustomEvent('drag_room_end', { detail: info }));

        this.#dragging_room = null;
        this.#dragging_room_wireframe.material.color.set(this.#dragging_room_wireframe.userData.originalColor);
        this.#dragging_room_wireframe = null;

        this.playPop(2);
    }

    /**
     * @param {Object} event
     * @param {THREE.Object3D} event.object
     */
    #onHoverOn(event) {
        const info = this.#getInfoStanza(event.object);
        this.dispatchEvent(new CustomEvent('hover_room_on', {detail: info}));
        if (this.debug) {
            const color = 'color: #' + GiocoVilla.getRoomColor(info.level).toString(16).padStart(6, '0');
            console.log('🟩️ %chover on%c \t%c%s%c, %d, %s', 'font-weight: bold; font-style: italic', 'font-weight: normal; font-style: normal', color, info.room.name, 'color: inherit', info.level, info.room.uuid);
        }
    }
    /**
     * @param {Object} event
     * @param {THREE.Object3D} event.object
     */
    #onHoverEnd(event) {
        // this.canvas.title = '';
        const info = this.#getInfoStanza(event.object);
        this.dispatchEvent(new CustomEvent('hover_room_off', { detail: info }));
        if (this.debug) {
            const color = 'color: #' + GiocoVilla.getRoomColor(info.level).toString(16).padStart(6, '0');
            console.log('⬜ %chover off \t%c%c%s%c, %d, %s', 'font-weight: bold; font-style: italic', 'font-weight: normal; font-style: normal', color, info.room.name, 'color: inherit', info.level, info.room.uuid);
        }
    }
    // endregion

    // region Eventi SVG
    /**
     * Gestisce il doppio click su una stanza nella mappa SVG.
     * @param {MouseEvent|TouchEvent} event
     */
    #onSVGDoubleClick(event) {
        if (this.#completeVisible) {
            return;
        }
        this.#dControls.clearPending();
        const stanzaSVG = this.#targetOfEvent(event).instance;
        const stanza = this.#rooms.getObjectByProperty('uuid', stanzaSVG.remember('uuid'));
        let timer = stanzaSVG.remember('timer_drag');
        if (timer) {
            window.clearTimeout(timer);
            stanzaSVG.forget('timer_drag');
        }
        if (this.#rotateRoom(stanza)) {
            this.test();
        }
    }

    /**
     * @param {MouseEvent} event
     */
    #onSVGHoverOn(event) {
        if (this.#completeVisible) {
            return;
        }
        const stanzaSVG = this.#targetOfEvent(event).instance;
        const stanza = this.#rooms.getObjectByProperty('uuid', stanzaSVG.remember('uuid'));
        return this.#onHoverOn({ object: stanza });
    }
    /**
     * @param {MouseEvent} event
     */
    #onSVGHoverEnd(event) {
        if (this.#completeVisible) {
            return;
        }
        const stanzaSVG = this.#targetOfEvent(event).instance;
        const stanza = this.#rooms.getObjectByProperty('uuid', stanzaSVG.remember('uuid'));
        return this.#onHoverEnd({ object: stanza });
    }

    /**
     * Gestisce il mouse down su una stanza nella mappa SVG avviando il trascinamento.
     * @param {MouseEvent|TouchEvent} event
     */
    #onSVGMouseDown(event) {
        // Prevent browser drag behavior as soon as possible
        event.preventDefault();
        // Prevent propagation to a parent that might also have dragging enabled
        event.stopPropagation();

        if (this.#completeVisible) {
            return;
        }
        const stanzaSVG = this.#targetOfEvent(event).instance;
        const stanza = this.#rooms.getObjectByProperty('uuid', stanzaSVG.remember('uuid'));

        let p = this.#clientPointOfEvent(event);
        // Converte le coordinate del mouse in quelle dell'SVG.
        let svgGlobal = p.matrixTransform(this.#svg.node.getScreenCTM().inverse());

        // Scostamento tra l'origine dell'elemento e il punto del click.
        let shiftX = svgGlobal.x - stanzaSVG.attr('x');
        let shiftY = svgGlobal.y - stanzaSVG.attr('y');

        stanzaSVG.remember({
            shiftX: shiftX,
            shiftY: shiftY
        });

        let timer = stanzaSVG.remember('timer_drag');
        if (timer) {
            window.clearTimeout(timer);
        }
        this.#svgDragged = stanzaSVG;
        // Ritarda il trascinamento in modo di intercettare prima il doppio click.
        timer = window.setTimeout(() => {
            if (this.debug) {
                console.info('onSVGMouseDown', event);
            }
            stanzaSVG.css({ cursor: 'move' });
            stanzaSVG.forget('timer_drag');
            window.addEventListener('mousemove', this.#svg_onMouseMove );
            window.addEventListener('touchmove', this.#svg_onMouseMove );
            this.#onDragStart({ object: stanza });
        }, 200);
        window.addEventListener('mouseup', this.#svg_onMouseUp);
        window.addEventListener('touchend', this.#svg_onMouseUp);
        stanzaSVG.remember('timer_drag', timer);
    }

    /**
     * @param {MouseEvent|TouchEvent} event
     */
    #onSVGMouseUp(event) {
        if (this.debug) {
            console.info('onSVGMouseUp', event);
        }
        window.removeEventListener('mouseup', this.#svg_onMouseUp);
        window.removeEventListener('touchend', this.#svg_onMouseUp);

        const stanzaSVG = this.#svgDragged;
        if (!stanzaSVG) {
            return;
        }
        const stanza = this.#rooms.getObjectByProperty('uuid', stanzaSVG.remember('uuid'));
        stanzaSVG.css({ cursor: 'pointer' });

        let timer = stanzaSVG.remember('timer_drag');
        if (timer) {
            window.clearTimeout(timer);
            stanzaSVG.forget('timer_drag');
            this.#svgDragged = null;
            return;
        }
        window.removeEventListener('mousemove', this.#svg_onMouseMove);
        window.removeEventListener('touchmove', this.#svg_onMouseMove);
        this.#onDragEnd({ object: stanza });
    };

    /**
     * @param {MouseEvent|TouchEvent} event
     */
    #onSVGMouseMove(event) {
        // Prevent browser drag behavior as soon as possible
        event.preventDefault();
        // Prevent propagation to a parent that might also have dragging enabled
        event.stopPropagation();

        const stanzaSVG = this.#svgDragged;
        const stanza = this.#rooms.getObjectByProperty('uuid', stanzaSVG.remember('uuid'));

        let p = this.#clientPointOfEvent(event);
        // Converte le coordinate del mouse in quelle dell'SVG.
        let svgGlobal = p.matrixTransform(this.#svg.node.getScreenCTM().inverse());

        const shiftX = stanzaSVG.remember('shiftX');
        const shiftY = stanzaSVG.remember('shiftY');
        // console.log(stanzaSVG, svgGlobal, event, svgGlobal.x - shiftX, svgGlobal.y - shiftY, stanzaSVG.attr('width'));

        const semi_width  = (this.#villa['base_w'] + 1) / 2;
        const semi_height = (this.#villa['base_h'] + 1) / 2;
        let x = Math.round((svgGlobal.x - shiftX + stanzaSVG.attr('width') / 2) / this.#svg_scale * 2) / 2;
        let y = Math.round((svgGlobal.y - shiftY + stanzaSVG.attr('height') / 2) / this.#svg_scale * 2) / 2;
        if (x < -semi_width) {
            x = -semi_width;
        } else if (x > semi_width) {
            x = semi_width;
        }
        if (y < -semi_height) {
            y = -semi_height;
        } else if (y > semi_height) {
            y = semi_height;
        }

        if (stanza.position.x !== x || stanza.position.z !== y) {
            stanza.position.x = x;
            stanza.position.z = y;
            this.#onDrag({ object: stanza });
        }
        return false;
    }
    // endregion

    // region Stanze
    /**
     * Valida la tipologia di una stanza.
     * @param {number} type
     * @returns {number}
     */
    static validateRoomType(type) {
        switch (type) {
            default:
            case 1:
                // Stanza quadrata
                return 1;
            case 2:
            case 23:
            case 32:
                // Stanza 2x3
                return 2;
            case 3:
            case 35:
            case 53:
                // stanza 3x5
                return 3;
        }
    }

    /**
     * Ottiene le dimensioni di una stanza in base alla sua tipologia.
     * @param {number} type
     * @param {boolean} [rotate] Indica se restituire le dimensioni di una stanza ruotata.
     * @returns {{w: number, h: number}}
     */
    static getRoomSize(type, rotate = false) {
        let w, h;
        switch (GiocoVilla.validateRoomType(type)) {
            default:
            case 1:
                w = 3;
                h = 3;
                break;
            case 2:
                w = 3;
                h = 2;
                break;
            case 3:
                w = 5;
                h = 3;
                break;
        }
        if (rotate) {
            const t = w;
            w = h;
            h = t;
        }
        return {w, h}
    }

    /**
     * Ottiene il colore di una tipologia di stanza.
     * @param {number} type Tipologia di stanza.
     * @returns {number}
     */
    static getRoomColor(type)
    {
        switch (type) {
            default:
            case 1:
                return 0x3333ff;
            case 2:
                return 0xFFA200;
            case 3:
                return 0xff0000;
        }
    }

    /**
     * Ottiene il colore del box a filo di ferro per la tipologia di stanza.
     * @param {number} type Tipologia di stanza.
     * @returns {number}
     */
    static getRoomWireColor(type)
    {
        switch (type) {
            default:
            case 1:
                return 0x7575FF;
            case 2:
                return 0xFFC560;
            case 3:
                return 0xFF8787;
        }
    }

    /**
     * Crea una stanza.
     * @param {number} type
     * @param {number} [h] Altezza della stanza. Se non specificato usa quella predefinita dal gioco.
     * @param {boolean} [round] Indica se creare una stanza con gli spigoli arrotondati.
     * @param {boolean} [bbox] Indica se creare il bound box della stanza.
     * @param {boolean} [svg] Indica se creare la corrispettiva stanza nella pianta SVG.
     * @returns {THREE.Object3D}
     */
    #createRoom(type, h = 0, round, bbox = true, svg = true) {
        type = GiocoVilla.validateRoomType(type);
        const size = GiocoVilla.getRoomSize(type, false);
        const color = GiocoVilla.getRoomColor(type);
        const wire_color = GiocoVilla.getRoomWireColor(type);
        h = h || this.#villa.h;
        const cGeometry0 = new THREE.BoxGeometry(size.w, h, size.h);
        round = round !== undefined ? round : this.options.roundbox;
        const cGeometry = round ? new RoundedBoxGeometry(size.w, h, size.h, 2, .1) : cGeometry0;
        // const cMaterial = new THREE.MeshStandardMaterial({ color: color });
        const cMaterial = new THREE.MeshPhongMaterial({
            color: color,
            //polygonOffset: true,
            //polygonOffsetFactor: 1, // positive value pushes polygon further away
            //polygonOffsetUnits: 1
        });

        cMaterial.transparent = true; // Abilita la visualizzazione di materiali semi trasparenti.

        const x = 0, z = 0;
        this.#room_counter += 1;
        const cube = new THREE.Mesh(cGeometry, cMaterial)
        cube.name = "stanza " + size.w + 'x' + size.h + " [" + this.#room_counter + ']';
        cube.position.set(x, h / 2, z);
        cube.castShadow = true;
        cube.receiveShadow = true;
        cube.userData.currentPosition = cube.position.clone();
        cube.userData.isRoom = true;
        cube.userData.level = type;
        cube.userData.w = size.w;
        cube.userData.h = size.h;
        cube.userData.counter = this.#room_counter;
        cube.userData.gioco = true;

        if (bbox) {
            // Aggiunge un secondo solido visualizzato in modalità wireframe.
            const geo = new THREE.EdgesGeometry(cGeometry0); // or WireframeGeometry
            const mat = new THREE.LineBasicMaterial({color: wire_color});
            mat.transparent = true;
            mat.opacity = 0;
            const wireframe = new THREE.LineSegments(geo, mat);
            wireframe.name = 'wireframe';
            wireframe.userData.originalColor = wire_color;
            wireframe.layers.set(LAYER_UI);
            cube.add(wireframe);
        }

        if (svg) {
            cube.userData.svg = this.#svg.rect(size.w * this.#svg_scale, size.h * this.#svg_scale)
                .attr({
                    fill: '#' + GiocoVilla.getRoomColor(type).toString(16).padStart(6, '0'),
                    x: (x - size.w / 2) * this.#svg_scale,
                    y: (z - size.h / 2) * this.#svg_scale,
                    stroke: '#333333',
                    'stroke-width': 1,
                })
                .on('dblclick', (event) => {
                    return this.#onSVGDoubleClick(event);
                })
                .on('mouseenter', (event) => {
                    return this.#onSVGHoverOn(event);
                })
                .on('mouseleave', (event) => {
                    return this.#onSVGHoverEnd(event);
                })
                .on('mousedown', (event) => {
                    return this.#onSVGMouseDown(event);
                })
                .on('touchstart', (event) => {
                    return this.#onSVGMouseDown(event);
                })
                .on('dragstart', (event) => {
                    // Prevent browser drag behavior as soon as possible
                    event.preventDefault();
                    // Prevent propagation to a parent that might also have dragging enabled
                    event.stopPropagation();
                    return false;
                })
                .on('dragstart.namespace', (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    return false;
                })
                .remember('uuid', cube.uuid)
                .css({cursor: 'pointer'})
            ;
            if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                /* Based on this http://jsfiddle.net/brettwp/J4djY/*/
                console.log('registro...', cube.userData.svg);
                const f = (event) => { this.#onSVGDoubleClick(event); }
                this.#registerDoubleTapClosure(cube.userData.svg.node, f);
            }
        }
        return cube;
    }

    /**
     * Aggiunge una stanza alla scena.
     * @param {number} type Tipologia di stanza da aggiungere.
     * @param {number} [x] Centro della stanza.
     * @param {number} [y]
     * @param {number} [z]
     * @param {boolean} [r] Indica se ruotare la stanza.
     * @returns {THREE.Object3D}
     */
    addRoom(type, x, y, z, r = false) {
        const stanza = this.#createRoom(type);

        x = x !== undefined ? x : this.#villa.new_origin.x;
        z = z !== undefined ? z : this.#villa.new_origin.z;
        stanza.position.set(x, this.#villa.h * 3 / 2, z);
        if (r) {
            stanza.rotation.y = Math.PI / 2;
        }

        // Aggiunge la stanza alla scena.
        this.#rooms.add(stanza);
        this.#requestRefresh();

        // Riproduce un feedback sonoro.
        this.playPop(2);

        // Aggiorna le informazioni sulla stanza.
        const info = this.#refreshInfoStanza(stanza);

        // Anima il posizionamento della stanza.
        gsap.to(stanza.position, {
            duration: .15,
            y: y !== undefined ? y : this.#villa.new_origin.y + this.#villa.h / 2,
            onComplete: () => {
                this.stopContinuousRefresh();
                if (this.#rooms.children.length === 1) {
                    this.test(true);
                }
            }
        });

        this.#refreshSVGPosition(info);

        return stanza;
    }

    /**
     * Rimuove una stanza dal gioco.
     * @param {THREE.Object3D} stanza
     * @param {boolean} [test] Indica se testare la soluzione del gioco dopo la rimozione della stanza.
     */
    removeRoom(stanza, test = true)
    {
        let y = stanza.position.y > this.#villa.h / 2 ? this.#villa.h / 2 - this.#villa.h_ground : this.#villa.h * 3 / 2;
        return new Promise((resolve, reject) => {
            // Sfuma l'opacità.
            gsap.to(stanza.material, { duration: .15, opacity: 0 });
            // Anima la posizione.
            gsap.to(stanza.position, { duration: .15, y: y, onComplete: () => {
                    this.#sagoma.visible = false;
                    this.#rooms.remove(stanza);
                    if (stanza.userData.svg) {
                        stanza.userData.svg.remove();
                        delete stanza.userData.svg;
                    }
                    this.#aree_stanze.delete(stanza.uuid);
                    this.stopContinuousRefresh();
                    this.#requestRefresh();
                    if (test) {
                        this.test(this.#rooms.children.length === 0);
                    }
                    resolve();
                } });
            }
        );
    }

    /**
     * Elimina tutte le stanze dalla scena.
     * @returns {Promise}
     */
    resetRooms() {
        // TODO: far iniziare leggermente in differita la rimozione di ogni stanza.
        return new Promise((resolve, reject) => {
            const c = this.#rooms.children.length;
            let n = 0;
            if (this.#rooms.children.length === 0) {
                resolve();
                return;
            }

            // Elimina le stanze.
            this.#rooms.children.forEach((stanza) => {
                // Anima la posizione della stanza.
                gsap.to(stanza.position, {
                    duration: 0.25,
                    y: this.#villa.h * 3 / 2
                });
                // Anima la trasparenza della stanza.
                gsap.to(stanza.material, {
                    duration: .25,
                    opacity: 0,
                    onComplete: () => {
                        this.stopContinuousRefresh();
                        // Elimina la stanza dalla scena.
                        stanza.userData.svg.remove();
                        delete stanza.userData.svg;
                        this.#rooms.remove(stanza);
                        this.#aree_stanze.delete(stanza.uuid);
                        this.#requestRefresh();
                        n += 1;
                        if (c === n) {
                            resolve();
                        }
                    },
                    onInterrupt: () => {
                        this.stopContinuousRefresh();
                        // Elimina la stanza dalla scena.
                        stanza.userData.svg.remove();
                        delete stanza.userData.svg;
                        this.#rooms.remove(stanza);
                        n += 1;
                        if (c === n) {
                            resolve();
                        }
                    },
                });
            });
            this.#aree_stanze.clear();
            this.#room_counter = 0;

            this.#setStatus(SolveEnum.ERR_INCOMPLETE);
        });
    }

    /**
     * Ottiene l'area della stanza. Se non è già stata calcolata la crea. Se esiste già __non__ la aggiorna.
     * @param {THREE.Object3D} room
     * @param {boolean} [refresh] Indica se aggiornare le informazioni
     * @returns {InfoStanza}
     */
    #getInfoStanza(room, refresh = false)
    {
        let info = this.#aree_stanze.get(room.uuid);
        if (!info) {
            info = new InfoStanza(room, this.#villa);
            this.#aree_stanze.set(room.uuid, info);
        } else if (refresh) {
            info.refresh();
        }
        return info;
    }

    /**
     * Aggiorna l'area della stanza. Se non è già stata calcolata la crea.
     * @param {THREE.Object3D} room
     * @returns {InfoStanza}
     */
    #refreshInfoStanza(room)
    {
        return this.#getInfoStanza(room, true);
    }

    /**
     * @param {InfoStanza} info
     */
    #refreshSVGPosition(info) {
        info.room.userData.svg
            .attr({
                width: info.size.w * this.#svg_scale,
                height: info.size.h * this.#svg_scale,
                x: (info.room.position.x - info.size.w / 2) * this.#svg_scale,
                y: (info.room.position.z - info.size.h / 2) * this.#svg_scale,
            })
            .front()
        ;
    }

    // endregion

    // region Logica del gioco

    /**
     * Valida la stanza selezionata durante un doppio click o il trascinamento.
     * @param object
     * @param log
     * @returns {{userData}|*|null}
     */
    #validaSelezioneStanza(object, log = false)
    {
        let p = object
        while (p) {
            if (p.userData.isStdRoom) {
                // log && console.debug('intersection with a standard room');
                return null;
            }
            p = p.parent;
        }
        p = object
        while (p && (!p.userData || !p.userData.isRoom)) {
            p = p.parent;
        }
        log && (p ? console.info('intersection with the room', p) : console.warn('no room found'));
        return p;
    }

    /**
     * Mostra la soluzione del gioco predefinita.
     */
    risolvi() {
        const stanze_soluzione = [];
        if (this.#rooms.children.length > 0) {
            /** Stanze che non soddisfano la soluzione. */
            const stanze_remove = [];

            // Identifica le stanze che già soddisfano la soluzione.
            for (let info of this.#aree_stanze.values()) {
                if (info.pos !== StanzaPos.INSIDE_PLAN) {
                    // Stanza fuori dalla pianta.
                    stanze_remove.push(info);
                } else {
                    // Verifica se la stanza soddisfa la soluzione.
                    let found = false;
                    for (let i = 0, c = this.#villa.soluzioni[0].length; i < c; i++) {
                        const s = this.#villa.soluzioni[0][i];
                        if (info.level === s.sala && info.rotated === s.r && info.cenX === s.x && info.cenY === s.y) {
                            stanze_soluzione.push(i);
                            found = true;
                            break;
                        }
                    }
                    if (!found) {
                        stanze_remove.push(info);
                    }
                }
            }
            // Elimina le stanze che non soddisfano la soluzione.
            stanze_remove.forEach((info) => {
                // noinspection JSIgnoredPromiseFromCall
                this.removeRoom(info.room, false);
            });
        }

        // Aggiunge le stanze della soluzione.
        for (let i = 0, c = this.#villa.soluzioni[0].length; i < c; i++) {
            const s = this.#villa.soluzioni[0][i];
            if (stanze_soluzione.indexOf(i) >= 0) {
                // Stanza già presente.
                continue;
            }
            const stanza = this.addRoom(s.sala, s.x, this.#villa.h / 2, s.y, s.r);
            this.#refreshInfoStanza(stanza);
        }

        this.#setStatus(SolveEnum.SOLVE_PALLADIO, true, true);
        this.#requestRefresh();
    }

    /**
     * Verifica se la partita è risolta.
     * @returns {number}
     */
    test(force = false)
    {
        return this.#test0(true, false, force);
    }

    #test0(sendEvent, auto, force)
    {
        const old_risolto = this.isRisolto;
        this.#setStatus(this.#test(), sendEvent, auto, force);
        if (!old_risolto && this.isRisolto) {
            // Elimina tutte le stanze superflue al di fuori della pianta.
            for (let area of this.#aree_stanze.values()) {
                if (area.pos !== StanzaPos.INSIDE_PLAN) {
                    // noinspection JSIgnoredPromiseFromCall
                    this.removeRoom(area.room, false);
                }
            }
        }

        return this.#status;
    }

    /**
     * Verifica se il gioco è risolto
     * @returns {number}
     */
    #test() {
        if (this.debug) { console.group('Verifica della soluzione…'); }

        // Verifica se ci sono delle stanze che si intersecano
        const intersections = this.#checkAreeGiocoIntersections(true);

        // Ricolloca eventuali sale rimaste sollevate perché in precedenza si intersecavano con altre.
        for (let room of this.#rooms.children) {
            if (room.position.y <= this.#villa.h / 2) {
                // Stanza già sul piano.
                continue;
            }
            if (!intersections.has(room)) {
                gsap.to(room.position, { duration: .15, y: this.#villa.h / 2});
            }
        }

        for (let stanza of intersections.values()) {
            if (typeof stanza === 'string') {
                if (this.debug) {
                    console.warn('Intersezione con una stanza predefinita', stanza);
                    console.groupEnd();
                }
                return SolveEnum.ERR_OVERLAP;
            } else {
                let area = this.#aree_stanze.get(stanza.uuid);
                if (area.pos === StanzaPos.INSIDE_PLAN) {
                    // Rilevata una intersezione tra le stanze della pianta.
                    if (this.debug) {
                        console.warn('Ci sono stanze sovrapposte!');
                        console.groupEnd();
                    }
                    return SolveEnum.ERR_OVERLAP;
                }
            }
        }

        let r;

        // Verifica le soluzioni di Palladio.
        if (this.#testSoluzioniPalladio()) {
            r = SolveEnum.SOLVE_PALLADIO;
        } else {
            // Verifica soluzioni alternative.
            r = this.#testAlternative();
        }
        if (this.debug) { console.groupEnd(); }
        return r;
    }

    /**
     * Verifica se la partita soddisfa una soluzione di Palladio.
     * @returns {boolean}
     */
    #testSoluzioniPalladio()
    {
        // Considera solo le stanze all'interno della pianta della villa.
        const aree = [];
        for (let area of this.#aree_stanze.values()) {
            if (area.pos === StanzaPos.INSIDE_PLAN) {
                aree.push(area);
            }
        }
        const n_aree = aree.length;

        // Verifica le soluzioni standard.
        const soluzioni = this.#villa.soluzioni;
        for (let i = 0; i < soluzioni.length; i++) {
            const soluzione = soluzioni[i];
            if (soluzione.length !== n_aree) {
                // Il numero di stanze del gioco non corrisponde a quello della soluzione.
                continue;
            }
            let found = true;

            // Cerca se per ogni stanza della soluzione ne esiste una nella partita in corso.
            for (let j = 0 ; j < soluzione.length; j++) {
                const stanza = soluzione[j];
                const level = GiocoVilla.validateRoomType(stanza.sala);
                let found_room = false;
                for (let area of aree) {
                    if (area.level !== level) {
                        // Stanza non del tipo cercato.
                        continue;
                    }
                    if (area.room.userData.soluzione) {
                        // Stanza già conteggiata nella soluzione.
                        continue;
                    }
                    if ((stanza.r && area.room.rotation.y !== 0) || (!stanza.r && area.room.rotation.y === 0) && stanza.x === area.room.position.x && stanza.y === area.room.position.z) {
                        area.room.userData.soluzione = j+1;
                        if (this.debug) { console.log('stanza', j, level, 'trovata'); }
                        found_room = true;
                        break;
                    }
                }
                if (!found_room) {
                    found = false;
                    if (this.debug) { console.warn('stanza', j, level, 'non trovata'); }
                    break;
                }
            }

            for (let area of aree) {
                area.room.userData.soluzione = 0;
                delete area.room.userData.soluzione;
            }

            if (found) {
                if (this.debug) { console.log('la partita soddisfa la soluzione', i); }
                return true;
            }
        }
        return false;
    }

    #testAlternative() {
        if (!this.#rooms.children.length) {
            // Non ci sono stanze.
            return SolveEnum.ERR_INCOMPLETE;
        }

        const stanze_sx = [];
        const stanze_dx = [];

        const aree_paths = new ClipperLib.PathsD();
        // Identifica le stanze all'interno della pianta della villa suddividendole in base all'asse di simmetria verticale.
        for (let info of this.#aree_stanze.values()) {
            if (info.pos === StanzaPos.INSIDE_PLAN) {
                if (info.maxX < 0) {
                    stanze_sx.push(info);
                } else {
                    stanze_dx.push(info);
                }
                aree_paths.push_back(info.paths.get(0));
            }
        }

        const area_soluzioni = ClipperLib.AreaPaths(this.#villa.soluzioni_aree);
        let result = ClipperLib.Difference(aree_paths, this.#villa.soluzioni_aree, ClipperLib.FillRule.NonZero, 2);
        let area = ClipperLib.AreaPaths(result);
        GiocoVilla.releasePathsD(result);

        if (this.debug) {
            console.log('area da campire: %f', area_soluzioni);
            console.log('area che sborda dalla pianta: %f', area);
        }
        if (area > 0) {
            // Alcune stanze sporgono oltre le aree da campire.
            return SolveEnum.ERR_SALE;
        }

        result = ClipperLib.Intersect(aree_paths, this.#villa.soluzioni_aree, ClipperLib.FillRule.NonZero, 2);
        area = ClipperLib.AreaPaths(result);
        GiocoVilla.releasePathsD(result);
        if (this.debug) { console.log('area coperte %f', area); }
        if (area !== area_soluzioni) {
            // Non c'è una perfetta copertura della superficie delle soluzioni.
            if (this.debug) { console.log('Aree ancora da campire', area_soluzioni-area); }
            return SolveEnum.ERR_INCOMPLETE;
        }
        // Verifica la simmetria.
        if (stanze_sx.length !== stanze_dx.length) {
            // Il numero di stanze a sx è diverso da quello a destra
            return SolveEnum.ERR_SYMMETRY1;
        }

        // Ordina i gruppi di stanze in base all'ordinata e quindi l'ascissa.
        const sort = (a, b, d) => {
            const
                aX = d * (d > 0 ? a.minX : a.maxX),
                aY =     a.minY,
                bX = d * (d > 0 ? b.minX : b.maxX),
                bY =     b.minY;

            if (aY === bY) {
                if (aX === bX) {
                    return 0;
                } else {
                    return (aX < bX ? -1 : 1);
                }
            } else {
                return aY < bY ? -1 : 1;
            }
        };

        stanze_sx.sort((a, b) => { return sort(a, b,  -1) });
        stanze_dx.sort((a, b) => { return sort(a, b,  1) });
        if (this.debug) {
            console.group('stanze di sx');
            for (let stanza of stanze_sx) {
                console.log('l %d (%f, %f) (%d°)', stanza.level, stanza.maxX, stanza.minY, stanza.rotated ? 90 : 0);
            }
            console.groupEnd();
            console.group('stanze di dx');
            for (let stanza of stanze_dx) {
                console.log('l %d (%f, %f) (%d°)', stanza.level, stanza.minX, stanza.minY, stanza.rotated ? 90 : 0);
            }
            console.groupEnd();
        }
        for (let i = 0, c = stanze_sx.length; i<c; i++) {
            let stanza_sx = stanze_sx[i];
            let stanza_dx = stanze_dx[i];
            if (stanza_sx.level !== stanza_dx.level ||
                stanza_sx.rotated !== stanza_dx.rotated ||
                Math.abs(stanza_sx.maxX) !== Math.abs(stanza_dx.minX) ||
                stanza_sx.cenY !== stanza_dx.cenY
            ) {
                return SolveEnum.ERR_SYMMETRY2;
            }
        }

        return SolveEnum.SOLVE_ALTERNATIVE;
    }


    /**
     * Verifica le intersezioni della stanza.
     * @param {THREE.Object3D} stanza Stanza di cui verificare le intersezioni.
     * @param {boolean} [search_all]
     * @returns {Set<THREE.Object3D|string>} Restituisce un array di Mesh. Per le stanze standard restituisce il loro nome.
     */
    #checkRoomIntersections(stanza, search_all = false)
    {
        const intersezioni = new Set();

        let area1 = this.#getInfoStanza(stanza);
        if (this.debug) {
            console.group('Verifico intersezioni per la stanza %c`%s`%c, superficie %s x %s [area %f], path: %s …',
                'color: #'+GiocoVilla.getRoomColor(area1.level).toString(16).padStart(6, '0'),
                stanza.name,
                'color: inherit',
                this.#pointToString(area1.data[0]), this.#pointToString(area1.data[2]),
                ClipperLib.AreaPath(area1.paths.get(0)),
                this.#pathDToString(area1.paths.get(0))
            );
        }
        // Verifica le intersezioni con le altre stanze del gioco.
        for (let area2 of this.#aree_stanze.values()) {
            if (area1.room === area2.room) {
                // Non verifica la stanza con sé stessa.
                continue;
            }
            // if (this.debug) { console.time('Calcolo intersezioni'); }
            let solutions = ClipperLib.RectClip(area1.rect, area2.paths); // ClipperLib.Intersect(area1.paths, area2.paths, ClipperLib.FillRule.NonZero, 2);
            // if (this.debug) { console.timeEnd('Calcolo intersezioni'); }

            if (solutions.size() > 0) {
                if (this.debug) {
                    console.error('- Trovata intersezione con stanza %c%s%c %s %s: area %f',
                        'color: #' + GiocoVilla.getRoomColor(area2.level).toString(16).padStart(6, '0'),
                        area2.room.name,
                        'color: inherit',
                        this.#pointToString(area2.data[0]), this.#pointToString(area2.data[2]),
                        ClipperLib.AreaPaths(solutions)
                    );
                }
                intersezioni.add(area1.room);
                intersezioni.add(area2.room);
                if (!search_all) {
                    if (this.debug) { console.groupEnd(); }
                    return intersezioni;
                }
            }
        }
        // Verifica le intersezioni con le stanze standard della villa.
        for (let name in this.#villa.models) {
            if (!this.#villa.models.hasOwnProperty(name)) {
                continue;
            }
            const model = this.#villa.models[name];
            if (!model['plan']) {
                continue;
            }
            if (!model.paths) {
                // Crea il path della stanza standard per la prima volta.
                const points = [];
                for (let i = 0, c = model.plan.length; i < c; i += 2) {
                    points.push({x: model.plan[i], y: -model.plan[i + 1]});
                }
                model.path = GiocoVilla.createPathD(points, true);
                // console.info("%s: %s", name, this.#pathDToString(model.path));
                const paths = new ClipperLib.PathsD();
                paths.push_back(model.path);

                model.paths = paths;
            }
            // if (this.debug) { console.time('Calcolo intersezioni con la stanza '+name); }
            let solutions = ClipperLib.RectClip(area1.rect, model['paths']); // ClipperLib.Intersect(area1.paths, model['paths'], ClipperLib.FillRule.NonZero, 2);
            // if (this.debug) { console.timeEnd('Calcolo intersezioni con la stanza '+name); }
            if (solutions.size() > 0) {
                if (this.debug) { console.error('- Trovata intersezione con stanza standard %s', name); }
                intersezioni.add(area1.room);
                intersezioni.add(name);
                if (!search_all) {
                    if (this.debug) { console.groupEnd(); }
                    return intersezioni;
                }
            }
            solutions.delete();
        }

        if (this.debug) { console.groupEnd(); }
        return intersezioni;
    }

    /**
     * Verifica se ci sono delle intersezioni con la stanza indicata.
     * @param {THREE.Object3D} stanza
     * @returns {boolean}
     */
    #roomHasIntersections(stanza)
    {
        return this.#checkRoomIntersections(stanza, false).size > 0;
    }

    /**
     * Controlla le intersezioni delle stanze.
     * @param {boolean} [search_all] Se true, verifica tutte le stanze, altrimenti si prima alla prima intersezione trovata.
     * @returns {Set<THREE.Object3D|string>}
     */
    #checkAreeGiocoIntersections(search_all = false)
    {
        let intersezioni = new Set();
        if (this.#aree_stanze.size === 0) {
            return intersezioni;
        }

        if (this.debug) { console.group('Verifica intersezioni…'); }

        for (let area1 of this.#aree_stanze.values()) {
            const area_intersections = this.#checkRoomIntersections(area1.room, search_all);
            intersezioni = new Set([...intersezioni, ...area_intersections]);

            if (!search_all && intersezioni.size > 0) {
                if (this.debug) { console.groupEnd(); }
                return intersezioni;
            }
        }
        if (this.debug) { console.groupEnd(); }

        return intersezioni;
    }

    /**
     * Verifica se la è attiva la visualizzazione completa dell'edificio con la copertura e spazi annessi.
     * Se attiva viene inibita la gestione del drag e la rotazione delle stanze.
     * @returns {boolean}
     */
    get isCompleteVisible()
    {
        return this.#completeVisible;
    }
    set isCompleteVisible(state)
    {
        if (state) {
            this.showComplete();
        } else {
            this.hideComplete();
        }
    }

    /**
     * Mostra l'edificio completo.
     */
    showComplete() {
        this.#completeVisible = true;
        this.#dControls.enabled = false;
        // Abilita la visualizzazione del layer.
        this.#camera.layers.enable(LAYER_COPERTURA);
        if (this.isRisolto && this.#villa.piani > 1) {
            // Crea le stanze dei piani superiori.
            for (let area of this.#aree_stanze.values()) {
                let stanza = this.#createRoom(area.level, this.#villa.h_piano1, this.options.roundbox, false, false);
                if (area.rotated) {
                    stanza.rotation.y = Math.PI / 2;
                }
                stanza.position.set(area.room.position.x, area.room.position.y + (this.#villa.h + this.#villa.h_piano1)/2, area.room.position.z);
                stanza.userData.piano1 = true;
                stanza.layers.set(LAYER_COPERTURA);
                this.#scene.add(stanza);
            }
        }
        // Scala il piano di base in modo di includere eventuali barchesse.
        this.#ground.scale.set(this.#villa.base_w2_scale || 1, 1, this.#villa.base_h2_scale || 1);
        this.#requestRefresh();
    }

    /**
     * Nasconde l'edificio completo.
     */
    hideComplete() {
        this.#completeVisible = false;
        this.#dControls.enabled = true;
        // Disabilita la visualizzazione del layer.
        this.#camera.layers.disable(LAYER_COPERTURA);
        let i = this.#scene.children.length -1;
        if (this.#villa.piani > 1) {
            while (i >= 0) {
                // Elimina le stanze dei piani superiori
                let g = this.#scene.children[i];
                if (g.userData.piano1) {
                    this.#scene.remove(g);
                }
                i--;
            }
        }
        // Ripristina la dimensione del piano.
        this.#ground.scale.set(1, 1, 1);
        this.#requestRefresh();
    }

    /**
     * Alterna la visualizzazione dell'edificio completo.
     * @returns {boolean} Restituisce se è visibile o meno la soluzione completa.
     */
    toggleComplete() {
        this.#completeVisible ? this.hideComplete() : this.showComplete();
        return this.#completeVisible
    }

    // endregion


    // region Utility
    /**
     * Riproduce l'audio di un pop.
     * @param n
     */
    playPop(n) {
        const sound = n === 2 ? this.#popAudio2 : this.#popAudio1;
        if (sound.isPlaying) {
            try {
                sound.stop();
            } catch (e) {}
        }
        try {
            sound.play();
        } catch (e) {

        }
    }

    #pointToString(point) {
        return '('+point.x+', '+point.y+')';
    }
    #pathDToString(path) {
        if (!path) {
            return '';
        }
        let s = '';
        for (let i = 0, size = path.size(); i < size; i++) {
            const p = path.get(i);
            s += this.#pointToString(p) + ', ';
        }
        return s.substring(0, s.length - 2);
    }

    /**
     * Ottiene un PathD da un array di punti.
     * Ricordarsi di rilasciare con PathD.delete().
     * @param {[{x: number, y: number}]} points
     * @param {boolean} [makePositive] Indica se invertire il path se non è positivo.
     * @returns {ClipperLib.PathD}
     */
    static createPathD(points, makePositive = true) {
        let s = "";
        points.forEach((point) => { s += point.x+','+point.y+', '; });
        s = s.substring(0, s.length-1);
        const path = ClipperLib.MakePathD(s);
        if (makePositive && !ClipperLib.IsPositive(path)) {
            ClipperLib.ReversePath(path);
        }
        return path;
    }

    /**
     * Rilascia un PathD
     * @param {ClipperLib.PathD} path
     */
    static releasePathD(path) {
        if (!path) {
            return;
        }
        path.delete();
    }

    /**
     *
     * @param {ClipperLib.PathsD} paths
     */
    static releasePathsD(paths) {
        if (!paths) {
            return;
        }
        for (let i = 0, c = paths.size(); i < c; i++) {
            GiocoVilla.releasePathD(paths.get(i));
        }
        paths.delete();
    }

    /**
     * Ottiene un PathD da un array di coordinate.
     * Ricordarsi di rilasciare il PathD.delete().
     * @param {[number]} points
     * @returns {ClipperLib.PathD}
     */
    #createPathDFromArray(points) {
        let s = points.join(", ");
        return ClipperLib.MakePathD(s);
    }

    /**
     * @param {MouseEvent|TouchEvent} event
     * @returns {DOMPoint}
     */
    #clientPointOfEvent(event) {
        let clientX, clientY;
        if (window.TouchEvent && event instanceof TouchEvent && event.touches.length > 0) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else {
            clientX = event.clientX || 0;
            clientY = event.clientY || 0;
        }
        return new DOMPoint(clientX, clientY);
    }

    /**
     *
     * @param {MouseEvent|TouchEvent} event
     * @returns {EventTarget}
     */
    #targetOfEvent(event) {
        return event.target;
    }
    // endregion


    // region Rendering

    resetView() {
        this.#oControls.reset();
    }
    topView() {
        this.#oControls.object.position.set(0, 40, 0);
        this.#oControls.object.updateProjectionMatrix();
        this.#oControls.update();
    }
    frontView() {
        this.#oControls.object.position.set(0, 2, 40);
        this.#oControls.object.updateProjectionMatrix();
        this.#oControls.update();
    }

    fitView(fitOffset) {
        // TODO: invece di questo codice che tende ad allontanare troppo la vista si potrebbe:
        //  - traslare la camera in modo di centrale il target con il centro
        //  - avvicinare/allontanare la camera lungo il suo asse (target/posizione) in modo che la distanza sia la stessa usata per frontView e topView

        fitOffset = fitOffset || .8;

        const camera = this.#oControls.object;

        const boundingBox = new THREE.Box3();
        boundingBox.setFromObject( this.#ground );

        const center = boundingBox.getCenter( new THREE.Vector3() );
        const size = boundingBox.getSize( new THREE.Vector3() );

        const maxSize = Math.max(size.x, size.y, size.z);
        const fitHeightDistance = maxSize / (2 * Math.atan(Math.PI * camera.fov / 360));
        const fitWidthDistance = fitHeightDistance / camera.aspect;
        const distance = fitOffset * Math.max(fitHeightDistance, fitWidthDistance);

        const direction = this.#oControls.target.clone()
            .sub(camera.position)
            .normalize()
            .multiplyScalar(distance);

        this.#oControls.maxDistance = distance * 10;
        this.#oControls.target.copy(center);

        camera.near = distance / 100;
        camera.far = distance * 100;
        camera.updateProjectionMatrix();

        camera.position.copy(this.#oControls.target).sub(direction);

        this.#oControls.update();
    }

    /**
     * Aggiorna la vista immediatamente.
     */
    refresh() {
        if (this.#stats) {
            this.#stats.begin();
        }
        this.#renderer.render(this.#scene, this.#camera);
        if (this.#stats) {
            this.#stats.end();
        }
    }

    /**
     * Aggiorna la vista al prossimo tick.
     */
    #requestRefresh() {
        if (!this.#isContinuousRefresh) {
            window.requestAnimationFrame(() => { this.refresh() });
        }
    }

    /**
     * Aggiorna la vista in continuazione.
     */
    #animate() {
        this.#isContinuousRefresh = true;
        this.#animationFrameId = window.requestAnimationFrame(() => { this.#animate(); });
        this.refresh();
    }

    /**
     * Avvia l'aggiornamento continuo della vista.
     * Viene incrementato un contatore a ogni chiamata della funzione.
     * Deve essere bilanciata tramite stopContinuousRefresh.
     * @see stopContinuousRefresh
     */
    startContinuousRefresh() {
        this.#continuousRefreshCounter += 1;
        if (this.debug) {
            if (this.#continuousRefreshCounter === 1) {
                console.info('▶️ startContinuousRefresh');
            } else {
                console.info('▶️ incremento startContinuousRefresh a %d', this.#continuousRefreshCounter);
            }
        }
        if (this.#isContinuousRefresh) {
            return;
        }
        this.#animate();
    }

    /**
     * Decrementa il contatore di aggiornamento automatico.
     * Quando raggiunge lo zero viene sospeso.
     * @see startContinuousRefresh
     */
    stopContinuousRefresh() {
        this.#continuousRefreshCounter = Math.max(0, this.#continuousRefreshCounter - 1);
        if (this.debug) {
            if (this.#continuousRefreshCounter > 0) {
                console.info('▶️ decremento stopContinuousRefresh a %d', this.#continuousRefreshCounter);
            } else {
                console.info('⏹️ stopContinuousRefresh');
            }
        }
        if (!this.#isContinuousRefresh || this.#continuousRefreshCounter > 0) {
            return;
        }
        window.cancelAnimationFrame(this.#animationFrameId);
        this.#isContinuousRefresh = false;
        this.#animationFrameId = 0;
    }

    /**
     * Numero di stanze aggiunte nel gioco.
     * @returns {number}
     */
    get roomsCount() {
        return this.#rooms.children.length;
    }
    // endregion

    // region Persistenza
    /**
     * Carica i dati di una partita.
     * @param {Array} s
     */
    loadPartita(s) {
        this.resetRooms().then(() => {
            for (let i = 0; i < s.length; i++) {
                const room = s[i];
                const stanza = this.#createRoom(room.level);
                if (room.r) {
                    stanza.rotation.y = Math.PI / 2;
                }
                stanza.position.set(room.x, this.#villa.h / 2, room.y);
                const info = this.#refreshInfoStanza(stanza);
                if (info.pos === StanzaPos.OUTSIDE_PLAN) {
                    stanza.position.y -= this.#villa.h_ground;
                }
                this.#rooms.add(stanza);
                this.#refreshSVGPosition(info);
                if (info.pos === StanzaPos.OUTSIDE_GROUND) {
                    // Questa condizione non dovrebbe mai verificarsi.
                    this.removeRoom(stanza, false).then(() => {});
                }
            }

            const intersections = this.#checkAreeGiocoIntersections(true);
            for (let room of intersections.values()) {
                if (typeof room === 'string') {
                    continue;
                }
                room.position.y = this.#villa.h * 3 / 2;
            }

            this.#requestRefresh();
            this.#test0(true, true, true);
        });
    }

    /**
     * Salva lo stato attuale della partita.
     * @returns {Array}
     */
    savePartita() {
        const s = [];
        for (let area of this.#aree_stanze.values()) {
            s.push({
                level: area.level,
                r: area.rotated,
                x: area.room.position.x,
                y: area.room.position.z,
            })
        }
        return s;
    }

    // endregion
}

export { GiocoVilla, SolveEnum }
